// Personal Member Search Form
import React, { useState, useEffect, useRef } from "react";
import { Card } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parse, format, getYear, getMonth, subYears } from "date-fns";
import STRINGS from "../../../Constants/Strings";
import {
  createNameInputHandler,
  createZipInputHandler,
  createCountryInputHandler,
  createNumericInputHandler,
  createEmailInputHandler,
  createPhoneInputHandler,
  createSinInputHandler,
} from "../utils/inputHandlers";

const PersonalMemberSearch = ({
  search,
  handleInputChange,
  handleSearch,
  handleReset,
  handleKeyDown,
  provinceList,
  searchError,
  emailError,
  setEmailError,
  mobileError,
  setMobileError,
  homeError,
  setHomeError,
  sin4Error,
  setSin4Error,
  zipError,
  setZipError,
}) => {
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [shouldScrollOnSubmit, setShouldScrollOnSubmit] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    email: false,
    mobile: false,
    home: false,
    sin4: false,
    zip: false,
  });

  // refs for accessibility & focus management
  const formRef = useRef(null);
  const topErrorRef = useRef(null);
  const firstNameRef = useRef(null);
  const advancedToggleRef = useRef(null);

  // Create input handlers
  const handleFirstNameInput = createNameInputHandler(
    "firstName",
    handleInputChange
  );
  const handleMiddleNameInput = createNameInputHandler(
    "middleName",
    handleInputChange
  );
  const handleLastNameInput = createNameInputHandler(
    "lastName",
    handleInputChange
  );
  const handleZipInput = createZipInputHandler(
    search,
    handleInputChange,
    setZipError
  );
  const handleCountryInput = createCountryInputHandler(
    search,
    handleInputChange
  );
  const handleEmailInput = createEmailInputHandler(
    handleInputChange,
    setEmailError
  );
  const handleMobileInput = createPhoneInputHandler(
    "mobile",
    setMobileError,
    handleInputChange
  );
  const handleHomeInput = createPhoneInputHandler(
    "home",
    setHomeError,
    handleInputChange
  );
  const handleSinInput = createSinInputHandler(setSin4Error, handleInputChange);
  const handlePanInput = createNumericInputHandler(
    "pan",
    16,
    handleInputChange
  );

  const handleMemberNumberInput = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
    handleInputChange({
      target: { name: "memberNumber", value },
    });
  };

  useEffect(() => {
    try {
      if (
        document.head &&
        !document.head.querySelector("style[data-datepicker-fix]")
      ) {
        const datePickerStyle = document.createElement("style");
        datePickerStyle.setAttribute("data-datepicker-fix", "true");
        datePickerStyle.textContent = `
          .react-datepicker-popper { z-index: 99999 !important; }
          .react-datepicker { z-index: 99999 !important; }
          .react-datepicker__portal { z-index: 99999 !important; }
        `;
        document.head.appendChild(datePickerStyle);
      }
    } catch (error) {
      console.warn("Failed to add datepicker styles:", error);
    }
  }, []);

  useEffect(() => {
    const inputNames = ["sin4", "mobile", "home", "email", "zip", "firstName"];

    // Initial clean-up on load
    inputNames.forEach((name) => {
      const inputEl = document.querySelector(`[name="${name}"]`);
      const errorEl = document.getElementById(`${name}-error`);
      if (inputEl) inputEl.classList.remove("invalid-input");
      if (errorEl) errorEl.style.visibility = "hidden";
    });

    setFieldErrors({
      email: false,
      mobile: false,
      home: false,
      sin4: false,
      zip: false,
    });

    if (firstNameRef.current) {
      firstNameRef.current.focus({ preventScroll: true });
    }

    // Listener: hides errors when typing
    const handleInput = (e) => {
      const target = e.target;
      const err = document.getElementById(`${target.name}-error`);
      if (err) err.style.visibility = "hidden";
      target.classList.remove("invalid-input");
    };

    const inputs = inputNames
      .map((name) => document.querySelector(`[name="${name}"]`))
      .filter(Boolean);

    inputs.forEach((input) => input.addEventListener("input", handleInput));

    return () => {
      inputs.forEach((input) =>
        input.removeEventListener("input", handleInput)
      );
    };
  }, []);

  useEffect(() => {
    if (!shouldScrollOnSubmit) return;

    const firstInvalid = document.querySelector(".invalid-input");
    if (!firstInvalid) {
      setShouldScrollOnSubmit(false);
      return;
    }

    // Sticky header offset: 12em
    const fontSize =
      parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const offset = 12 * fontSize;
    const rect = firstInvalid.getBoundingClientRect();
    const y = rect.top + window.scrollY - offset;

    window.scrollTo({ top: y, behavior: "smooth" });

    setTimeout(() => {
      firstInvalid.focus({ preventScroll: true });
    }, 300);

    // disable scrolling after the one allowed scroll
    setShouldScrollOnSubmit(false);
  }, [
    emailError,
    mobileError,
    homeError,
    sin4Error,
    zipError,
    searchError,
    shouldScrollOnSubmit,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setShouldScrollOnSubmit(true);
    handleSearch();
  };

  const toggleAdvanced = () => {
    setShowAdditionalFields((v) => !v);
    if (!showAdditionalFields) {
      setTimeout(() => {
        const sinEl = document.querySelector('input[name="sin4"]');
        if (sinEl) sinEl.focus();
      }, 150);
    } else {
      if (advancedToggleRef.current) advancedToggleRef.current.focus();
    }
  };

  const onSearchClick = (e) => {
    handleSubmit(e);
  };

  return (
    <div className="[ row ]">
      <div className="[ col-xl-12 col-lg-12 col-12 ]">
        <section
          className="branch-widget branch-widget--members-search"
          aria-labelledby="personal-members-search-title"
        >
          <Card className="branch-widget__card">
            <Card.Body>
              <h2 id="personal-members-search-title" className="sr-only">
                {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM.TITLE}
              </h2>

              {/* Toggle */}
              <div className="[ row ]">
                <div className="col-12 d-flex justify-content-start">
                  <div className="[ mb-3 ]">
                    <button
                      type="button"
                      ref={advancedToggleRef}
                      className="branch-widget__action"
                      onClick={toggleAdvanced}
                      aria-expanded={showAdditionalFields}
                      aria-controls="advanced-search-fields"
                      aria-label={
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                          .ADVANCED_SEARCH_TOGGLE
                      }
                    >
                      <i className="fas fa-sliders-h mr-1" aria-hidden="true" />{" "}
                      {
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                          .ADVANCED_SEARCH_TOGGLE
                      }{" "}
                      <i
                        className={
                          showAdditionalFields
                            ? "fas fa-caret-down ml-1"
                            : "fas fa-caret-right ml-1"
                        }
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Form */}
              <form
                ref={formRef}
                onSubmit={onSearchClick}
                noValidate
                aria-describedby={searchError ? "search-error" : undefined}
              >
                <div className="[ row ]">
                  {/* First Name */}
                  <div className="[ col-12 col-lg-3 ]">
                    <div className="[ mb-3 ]">
                      <label htmlFor="firstName" className="form-label">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .FIRSTNAME_LABEL
                        }
                      </label>
                      <input
                        id="firstName"
                        ref={firstNameRef}
                        type="text"
                        className="form-control"
                        name="firstName"
                        value={search.firstName}
                        onChange={handleFirstNameInput}
                        onKeyDown={handleKeyDown}
                        maxLength={50}
                        aria-describedby="firstName-hint"
                      />
                      <small id="firstName-hint" className="sr-only">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .FIRSTNAME_HINT
                        }
                      </small>
                      <div className="[ error-msg ]" aria-live="polite"></div>
                    </div>
                  </div>

                  {/* Middle Name */}
                  <div className="[ col-12 col-lg-3 ]">
                    <div className="[ mb-3 ]">
                      <label htmlFor="middleName" className="form-label">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .MIDDLENAME_LABEL
                        }
                      </label>
                      <input
                        id="middleName"
                        className="form-control"
                        name="middleName"
                        value={search.middleName}
                        onChange={handleMiddleNameInput}
                        onKeyDown={handleKeyDown}
                        placeholder={
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .MIDDLENAME_PLACEHOLDER
                        }
                        maxLength={50}
                        aria-describedby="middleName-hint"
                      />
                      <small id="middleName-hint" className="sr-only">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .MIDDLENAME_HINT
                        }
                      </small>
                      <div className="[ error-msg ]" aria-live="polite"></div>
                    </div>
                  </div>

                  {/* Last Name */}
                  <div className="[ col-12 col-lg-3 ]">
                    <div className="[ mb-3 ]">
                      <label htmlFor="lastName" className="form-label">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .LASTNAME_LABEL
                        }
                      </label>
                      <input
                        id="lastName"
                        className="form-control"
                        name="lastName"
                        value={search.lastName}
                        onChange={handleLastNameInput}
                        onKeyDown={handleKeyDown}
                        maxLength={50}
                        aria-describedby="lastName-hint"
                      />
                      <small id="lastName-hint" className="sr-only">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .LASTNAME_HINT
                        }
                      </small>
                      <div className="[ error-msg ]" aria-live="polite"></div>
                    </div>
                  </div>

                  {/* DOB */}
                  <div className="[ col-12 col-lg-3 ]">
                    <div className="[ mb-3 ]">
                      <label htmlFor="dob" className="form-label">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .DOB_LABEL
                        }
                      </label>
                      <DatePicker
                        id="dob"
                        className="form-control"
                        dateFormat="MM-dd-yyyy"
                        placeholderText={
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .DOB_PLACEHOLDER
                        }
                        selected={
                          search.dob
                            ? parse(search.dob, "MM-dd-yyyy", new Date())
                            : null
                        }
                        open={isDatePickerOpen}
                        onClickOutside={() => setIsDatePickerOpen(false)}
                        onFocus={() => setIsDatePickerOpen(true)}
                        onChange={(date) => {
                          const formatted = date
                            ? format(date, "MM-dd-yyyy")
                            : "";
                          handleInputChange({
                            target: { name: "dob", value: formatted },
                          });
                          setIsDatePickerOpen(false);
                        }}
                        onChangeRaw={(e) => {
                          const { value } = e.target;
                          // Only allow numbers and hyphens for date format MM-dd-yyyy
                          const validChars = /^[0-9\-]*$/;
                          if (!validChars.test(value)) {
                            e.preventDefault();
                            return;
                          }
                          // Allow the change if it matches valid characters
                          // The DatePicker will handle validation of the actual date
                        }}
                        minDate={subYears(new Date(), 100)}
                        maxDate={subYears(new Date(), 12)}
                        popperProps={{
                          style: { zIndex: 99999 },
                          className: "datepicker-popper-high-z",
                        }}
                        popperPlacement="bottom-start"
                        aria-describedby="dob-hint"
                        renderCustomHeader={({
                          date,
                          changeYear,
                          changeMonth,
                          decreaseMonth,
                          increaseMonth,
                          prevMonthButtonDisabled,
                          nextMonthButtonDisabled,
                        }) => {
                          const years = Array.from(
                            { length: 100 },
                            (_, i) => getYear(new Date()) - 12 - i
                          );
                          const months = [
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_JAN,
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_FEB,
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_MAR,
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_APR,
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_MAY,
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_JUN,
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_JUL,
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_AUG,
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_SEP,
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_OCT,
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_NOV,
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .DOB_MONTHS_DEC,
                          ];
                          return (
                            <div className="custom-datepicker-header">
                              <div className="custom-datepicker-clear-section">
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleInputChange({
                                      target: { name: "dob", value: "" },
                                    });
                                    setIsDatePickerOpen(false);
                                  }}
                                  title={
                                    STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS
                                      .FORM.DOB_CLEAR_TITLE
                                  }
                                >
                                  {
                                    STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS
                                      .FORM.DOB_CLEAR_BUTTON
                                  }
                                </button>
                              </div>
                              <div className="custom-datepicker-nav-section">
                                <button
                                  type="button"
                                  onClick={decreaseMonth}
                                  disabled={prevMonthButtonDisabled}
                                >
                                  {"<"}
                                </button>
                                <select
                                  value={getYear(date)}
                                  onChange={(e) =>
                                    changeYear(Number(e.target.value))
                                  }
                                >
                                  {years.map((y) => (
                                    <option key={y} value={y}>
                                      {y}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  value={months[getMonth(date)]}
                                  onChange={(e) =>
                                    changeMonth(months.indexOf(e.target.value))
                                  }
                                >
                                  {months.map((m) => (
                                    <option key={m} value={m}>
                                      {m}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={increaseMonth}
                                  disabled={nextMonthButtonDisabled}
                                >
                                  {">"}
                                </button>
                              </div>
                            </div>
                          );
                        }}
                      />
                      <small id="dob-hint" className="text-muted">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .DOB_HINT
                        }
                      </small>
                      <div className="[ error-msg ]" aria-live="polite"></div>
                    </div>
                  </div>
                </div>

                {/* Advanced Fields */}
                <div
                  id="advanced-search-fields"
                  aria-hidden={!showAdditionalFields}
                  className="divider"
                >
                  {showAdditionalFields && (
                    <div className="[ drawer-content-container ] [ mb-3 ]">
                      {/* SIN */}
                      <div className="row mb-3">
                        <div className="col-12 col-lg-6 mb-3 mb-md-0">
                          <label htmlFor="sin4" className="form-label">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .SIN_LABEL
                            }
                          </label>
                          <input
                            id="sin4"
                            className={`form-control ${
                              sin4Error ? "invalid-input" : ""
                            }`}
                            name="sin4"
                            placeholder={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .SIN_PLACEHOLDER
                            }
                            value={search.sin4 || ""}
                            onChange={handleSinInput}
                            onKeyDown={handleKeyDown}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={9}
                            aria-invalid={!!sin4Error}
                            aria-describedby={
                              sin4Error ? "sin4-error sin4-hint" : "sin4-hint"
                            }
                            autoComplete="off" // prevents autofill styling
                          />
                          <small id="sin4-hint" className="text-muted">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .SIN_HINT
                            }
                          </small>
                          <div
                            id="sin4-error"
                            className="error-msg"
                            style={{ marginBottom: sin4Error ? "1rem" : "0" }}
                            role="alert"
                            aria-live="assertive"
                          >
                            {sin4Error}
                          </div>
                        </div>
                      </div>

                      <div className="[ divider ]" />

                      {/* PAN + Member ID */}
                      <div className="row mb-3">
                        <div className="col-md-6 mb-3 mb-md-0">
                          <label htmlFor="pan" className="form-label">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .PAN_LABEL
                            }
                          </label>
                          <input
                            id="pan"
                            className="form-control"
                            name="pan"
                            placeholder={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .PAN_PLACEHOLDER
                            }
                            value={search.pan || ""}
                            onChange={handlePanInput}
                            onKeyDown={handleKeyDown}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={16}
                            aria-describedby="pan-hint"
                          />
                          <small id="pan-hint" className="text-muted">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .PAN_HINT
                            }
                          </small>
                          <div
                            className="[ error-msg ]"
                            aria-live="polite"
                          ></div>
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="memberNumber" className="form-label">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .MEMBERID_LABEL
                            }
                          </label>
                          <input
                            id="memberNumber"
                            className="form-control"
                            name="memberNumber"
                            placeholder={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .MEMBERID_PLACEHOLDER
                            }
                            value={search.memberNumber || ""}
                            maxLength={12}
                            onChange={handleMemberNumberInput}
                            onKeyDown={handleKeyDown}
                            aria-describedby="memberNumber-hint"
                          />
                          <small id="memberNumber-hint" className="text-muted">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .MEMBERID_HINT
                            }
                          </small>
                          <div
                            className="[ error-msg ]"
                            aria-live="polite"
                          ></div>
                        </div>
                      </div>

                      <div className="[ divider ]" />

                      {/* Phones + Email */}
                      <div className="row mb-3">
                        <div className="col-12 col-lg-6 mb-3 mb-md-0">
                          <label htmlFor="mobile" className="form-label">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .MOBILE_LABEL
                            }
                          </label>
                          <input
                            id="mobile"
                            className={`form-control ${
                              mobileError ? "invalid-input" : ""
                            }`}
                            name="mobile"
                            placeholder={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .MOBILE_PLACEHOLDER
                            }
                            value={search.mobile || ""}
                            onChange={handleMobileInput}
                            onKeyDown={handleKeyDown}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength="10"
                            aria-invalid={!!mobileError}
                            aria-describedby={
                              mobileError
                                ? "mobile-error mobile-hint"
                                : "mobile-hint"
                            }
                          />
                          <small id="mobile-hint" className="text-muted">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .MOBILE_HINT
                            }
                          </small>
                          <div
                            id="mobile-error"
                            className="[ error-msg ]"
                            style={{ marginBottom: mobileError ? "1rem" : "0" }}
                            role="alert"
                            aria-live="assertive"
                          >
                            {mobileError}
                          </div>
                        </div>

                        <div className="col-12 col-lg-6 mb-3 mb-md-0">
                          <label htmlFor="home" className="form-label">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .HOME_LABEL
                            }
                          </label>
                          <input
                            id="home"
                            className={`form-control ${
                              homeError ? "invalid-input" : ""
                            }`}
                            name="home"
                            placeholder={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .HOME_PLACEHOLDER
                            }
                            value={search.home || ""}
                            onChange={handleHomeInput}
                            onKeyDown={handleKeyDown}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength="10"
                            aria-invalid={!!homeError}
                            aria-describedby={
                              homeError ? "home-error home-hint" : "home-hint"
                            }
                          />
                          <small id="home-hint" className="text-muted">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .HOME_HINT
                            }
                          </small>
                          <div
                            id="home-error"
                            className="[ error-msg ]"
                            style={{ marginBottom: homeError ? "1rem" : "0" }}
                            role="alert"
                            aria-live="assertive"
                          >
                            {homeError}
                          </div>
                        </div>

                        <div className="col-12">
                          <label htmlFor="email" className="form-label">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .EMAIL_LABEL
                            }
                          </label>
                          <input
                            id="email"
                            className={`form-control ${
                              emailError ? "invalid-input" : ""
                            }`}
                            name="email"
                            type="email"
                            placeholder={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .EMAIL_PLACEHOLDER
                            }
                            value={search.email || ""}
                            onChange={handleEmailInput}
                            onKeyDown={handleKeyDown}
                            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}"
                            maxLength="50"
                            aria-invalid={!!emailError}
                            aria-describedby={
                              emailError ? "email-error" : "email-hint"
                            }
                          />
                          <small id="email-hint" className="text-muted">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .EMAIL_HINT
                            }
                          </small>
                          <div
                            id="email-error"
                            className="[ error-msg ]"
                            style={{ marginBottom: emailError ? "1rem" : "0" }}
                            role="alert"
                            aria-live="assertive"
                          >
                            {emailError}
                          </div>
                        </div>
                      </div>

                      <div className="[ divider ]" />

                      {/* Address */}
                      <div className="mb-3">
                        <label htmlFor="address" className="form-label">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .ADDRESS_LABEL
                          }
                        </label>
                        <input
                          id="address"
                          className="form-control"
                          name="address"
                          placeholder={
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .ADDRESS_PLACEHOLDER
                          }
                          value={search.address || ""}
                          onChange={handleInputChange}
                          onKeyDown={handleKeyDown}
                          maxLength="100"
                          aria-describedby="address-hint"
                        />
                        <small id="address-hint" className="text-muted">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .ADDRESS_HINT
                          }
                        </small>
                        <div className="[ error-msg ]" aria-live="polite"></div>
                      </div>

                      {/* City, State, ZIP, Country */}
                      <div className="row mb-3">
                        <div className="col-md-3 mb-3 mb-md-0">
                          <label htmlFor="city" className="form-label">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .CITY_LABEL
                            }
                          </label>
                          <input
                            id="city"
                            className="form-control"
                            name="city"
                            placeholder={
                              search.country === "USA"
                                ? STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS
                                    .FORM.CITY_PLACEHOLDER_USA
                                : STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS
                                    .FORM.CITY_PLACEHOLDER_CAN
                            }
                            value={search.city || ""}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            maxLength="50"
                          />
                          <div
                            className="[ error-msg ]"
                            aria-live="polite"
                          ></div>
                        </div>

                        <div className="col-md-3 mb-3 mb-md-0">
                          <label htmlFor="state" className="form-label">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .STATE_LABEL
                            }
                          </label>
                          <select
                            id="state"
                            className="form-control"
                            name="state"
                            value={search.state || ""}
                            onChange={handleInputChange}
                          >
                            <option value="">
                              {
                                STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                  .STATE_SELECT_DEFAULT
                              }
                            </option>
                            {provinceList.map((p) =>
                              p.country === search.country ? (
                                <option key={p.code} value={p.code}>
                                  {p.description}
                                </option>
                              ) : null
                            )}
                          </select>
                          <div
                            className="[ error-msg ]"
                            aria-live="polite"
                          ></div>
                        </div>

                        <div className="col-md-3 mb-3 mb-md-0">
                          <label htmlFor="zip" className="form-label">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .ZIP_LABEL
                            }
                          </label>
                          <input
                            id="zip"
                            className="form-control"
                            name="zip"
                            placeholder={
                              search.country === "USA"
                                ? STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS
                                    .FORM.ZIP_PLACEHOLDER_USA
                                : STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS
                                    .FORM.ZIP_PLACEHOLDER_CAN
                            }
                            value={search.zip || ""}
                            onChange={handleZipInput}
                            onKeyDown={handleKeyDown}
                            maxLength={search.country === "USA" ? 5 : 7}
                            aria-invalid={!!zipError}
                            aria-describedby={
                              zipError ? "zip-error zip-hint" : "zip-hint"
                            }
                          />
                          <small id="zip-hint" className="text-muted">
                            {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM.ZIP_HINT(
                              search.country
                            )}
                          </small>
                          <div
                            id="zip-error"
                            className="[ error-msg ]"
                            style={{ marginBottom: zipError ? "1rem" : "0" }}
                            role="alert"
                            aria-live="assertive"
                          >
                            {zipError}
                          </div>
                        </div>

                        <div className="col-md-3">
                          <label htmlFor="country" className="form-label">
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                .COUNTRY_LABEL
                            }
                          </label>
                          <select
                            id="country"
                            className="form-control"
                            name="country"
                            value={search.country}
                            onChange={handleCountryInput}
                          >
                            <option value="CAN">
                              {
                                STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                  .COUNTRY_OPTION_CAN
                              }
                            </option>
                            <option value="USA">
                              {
                                STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                                  .COUNTRY_OPTION_USA
                              }
                            </option>
                          </select>
                          <div
                            className="[ error-msg ]"
                            aria-live="polite"
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="form-row mt-4 align-items-center justify-content-sm-end">
                  <div className="col-12 col-sm-auto mb-3 mb-sm-0">
                    <div
                      id="search-error"
                      className="error-msg"
                      role="alert"
                      aria-live="assertive"
                      tabIndex={-1}
                    >
                      {searchError}
                    </div>
                  </div>
                  <div className="col-12 col-sm-auto mb-3 mb-sm-0">
                    <button
                      type="reset"
                      className="btn btn-secondary btn-block"
                      onClick={(e) => {
                        handleReset(e);
                        setSubmitAttempted(false);
                        setFieldErrors({
                          email: false,
                          mobile: false,
                          home: false,
                          sin4: false,
                          zip: false,
                        });
                        if (firstNameRef.current) {
                          const offset =
                            12 *
                            parseFloat(
                              getComputedStyle(document.documentElement)
                                .fontSize
                            );
                          const top =
                            firstNameRef.current.getBoundingClientRect().top +
                            window.scrollY -
                            offset;
                          window.scrollTo({ top, behavior: "smooth" });
                          setTimeout(
                            () =>
                              firstNameRef.current.focus({
                                preventScroll: true,
                              }),
                            300
                          );
                        }
                      }}
                    >
                      {
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                          .RESET_BUTTON
                      }
                    </button>
                  </div>
                  <div className="col-12 col-sm-auto">
                    <button
                      type="submit"
                      className="btn btn-primary btn-block"
                      onClick={handleSubmit}
                      onKeyDown={handleKeyDown}
                    >
                      {
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                          .SEARCH_BUTTON
                      }
                    </button>
                  </div>
                </div>
              </form>
            </Card.Body>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default PersonalMemberSearch;
